import { merge } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { ThemeProvider, withTheme } from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { green, navy, white, grey10 } from '@zocdoc/branding/colors';
import { Metrics, MetricsClickable, CustomMetrics, EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

import ScrollLock from '../../../ScrollLock';
import PaddedContainer from '../../PaddedContainer';
import PoweredByZocdoc from '../PoweredByZocdocBadge';

import {
    AppStoreLink,
    BrowseLinkGroup,
    CitiesLinkGroup,
    FacebookLink,
    GooglePlayLink,
    GooglePlusLink,
    LinkedInLink,
    MadeInNYLink,
    SearchLinkGroup,
    SpecialtiesLinkGroup,
    TwitterLink,
} from '../../Links';

import CollapsibleSection from '../../../CollapsibleSection';
import { breakpoint, Breakpoints } from '../../../grid';

import Close from '../../../icons/Close';
import Hamburger from '../../../icons/Hamburger';
import MagnifyingGlass from '../../../icons/MagnifyingGlass';
import UnstyledArrow from '../../../icons/Arrow';

import { Portal } from '../../../Portal';
import { FontFamily } from '../../../typography';

import { USER_TYPE } from '../../constants';
import {
    getMobileUserLinks,
    DropdownLinks,
    LOCALIZED_STRINGS,
    WellGuideLinkVariants,
} from '../constants';
import { Link as ZdLink } from '../../../link';

class MobileMenu extends PureComponent {
    static propTypes = {
        children: PropTypes.any,
        user: PropTypes.object,

        // this is an escape hatch for screenshot testing, don't use it for anything else
        forceOpen: PropTypes.bool,

        theme: PropTypes.shape({
            header: PropTypes.shape({
                logo: PropTypes.shape({
                    linkUri: PropTypes.string,
                }),
                mobileMenu: PropTypes.shape({
                    links: PropTypes.arrayOf(
                        PropTypes.shape({
                            text: PropTypes.string.isRequired,
                            title: PropTypes.string,
                            uri: PropTypes.string.isRequired,
                        })
                    ).isRequired,
                    signIn: PropTypes.shape({
                        enabled: PropTypes.bool.isRequired,
                    }).isRequired,
                }).isRequired,
                showPoweredByZocdoc: PropTypes.bool.isRequired,
            }).isRequired,
        }).isRequired,
        wellGuideLinkVariant: PropTypes.string,
        onBeforeLinkClick: PropTypes.func,
        showUpcomingAppointmentsOnMobileMenu: PropTypes.bool,
    };

    static defaultProps = {
        forceOpen: false,
        showUpcomingAppointmentsOnMobileMenu: false,
        wellGuideLinkVariant: WellGuideLinkVariants.OFF,
    }

    constructor(props) {
        super(props);

        this.state = {
            open: props.forceOpen,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleWindowResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize, true);
    }

    render() {
        return (
            <div {...filterToValidAttributes(this.props)}>
                <Metrics
                    action="Click Mobile Menu Link"
                    section="BurgerMenu"
                >
                    {this.maybeRenderTrigger()}
                    {this.maybeRenderMenu()}
                </Metrics>
                {this.state.open && <ScrollLock />}
            </div>
        );
    }

    maybeRenderTrigger() {
        if (this.props.theme.header.mobileMenu.enabled) {
            return (
                <MenuTriggerArea>
                    <MetricsClickable
                        component="Trigger"
                        element="Trigger"
                    >
                        <MenuTrigger
                            data-test="mobile-menu-trigger"
                            onClick={this.toggleMenu}
                            title="Open the mobile navigation menu"
                        />
                        <div>
                            <Hamburger
                                color="currentColor"
                                height={11.531}
                            />
                        </div>
                    </MetricsClickable>
                </MenuTriggerArea>
            );
        }
    }

    maybeRenderMenu() {
        if (this.state.open) {
            return (
                <Portal
                    inline={this.props.forceOpen}
                    portalId="zd_mobile_menu"
                >
                    <Menu data-test="mobile-menu">
                        <Home>
                            <StyledClose
                                color="currentColor"
                                component={Close}
                                data-test="mobile-menu-close"
                                height={14}
                                onClick={this.toggleMenu}
                            />

                            <MagnifyingGlass
                                color="currentColor"
                                height={20}
                            />

                            <HomeLink
                                data-test="mobile-menu-home-link"
                                href={this.props.theme.header.logo.linkUri}
                            >
                                Find a doctor or dentist
                            </HomeLink>
                        </Home>

                        {this.maybeRenderUserSection()}
                        {this.props.children}
                        {this.maybeRenderCustomLinks()}
                        {this.renderSecondaryContent()}
                    </Menu>
                </Portal>
            );
        }
    }

    filterPatientLinks(links) {
        const {
            showUpcomingAppointmentsOnMobileMenu,
        } = this.props;

        if (showUpcomingAppointmentsOnMobileMenu) {
            return links;
        }

        return links.filter(link => link.message !== LOCALIZED_STRINGS.UpcomingAppointments);
    }

    getLinks() {
        const {
            wellGuideLinkVariant,
        } = this.props;

        const mobileUserLinks = getMobileUserLinks(wellGuideLinkVariant);

        switch (this.props.user.user_type) {
            case USER_TYPE.Patient:
                return this.filterPatientLinks(mobileUserLinks.Patient);
            case USER_TYPE.Provider:
                return mobileUserLinks.Provider;
            case USER_TYPE.ZdEmployee:
                return mobileUserLinks.ZdEmployee;
        }
    }

    maybeRenderUserSection() {
        if (this.props.theme.header.mobileMenu.signIn.enabled) {
            if (this.props.signedIn) {
                const links = this.getLinks();
                return this.renderUserLinks(links);
            } else {
                return this.renderSignIn();
            }
        }
    }

    renderUserLinks(links) {
        return (
            <UserSection>
                <UserLinkContainer data-test="mobile-menu-user-links">
                    {links.map(link => {
                        return this.renderUserLink(link);
                    })}
                </UserLinkContainer>
            </UserSection>
        );
    }

    renderUserLink(link) {
        const linkIcon = link.icon ? (
            <UserLinkIcon>
                <link.icon height={link.iconHeight || 30} />
            </UserLinkIcon>
        ) : null;

        const arrow = link.url !== DropdownLinks.signout ? (
            <StyledArrow
                direction="right"
                height={16}
            />
        ) : null;

        const trackingMessage = link.message.defaultMessage.replace(/ /g, '');
        return (
            <MetricsClickable
                key={link.dataTest}
                component="UserLink"
                element={trackingMessage}
            >
                <UserLink
                    data-element-name={trackingMessage}
                    data-test={`mobile-menu-${link.dataTest}`}
                    href={link.url}
                    onClick={this.trackUserLinks}
                >
                    {linkIcon}
                    <UserLinkText>
                        <FormattedMessage {...link.message} />
                    </UserLinkText>
                    {arrow}
                </UserLink>
            </MetricsClickable>
        );
    }

    renderSignIn() {
        return (
            <SignIn data-test="mobile-menu-signin-links">
                <Metrics component="SignInLinks">
                    <CustomMetrics>
                        {
                            ({ sendMetric }) => (
                                <SignInLink
                                    data-element-name="mobile-menu-patient-login"
                                    data-test="mobile-menu-patient-login"
                                    href={DropdownLinks.patientSignin}
                                    onClick={ev => {
                                        sendMetric({
                                            element: 'PatientLogin',
                                            initiator: EVENT_INITIATORS.USER,
                                            interactionType: INTERACTION_TYPES.CLICK,
                                        });
                                        this.trackSignInLinks(ev);
                                    }}
                                >
                                    <UserType>
                                        <FormattedMessage {...LOCALIZED_STRINGS.Patients} />
                                    </UserType>
                                    <AccountText>
                                        <FormattedMessage {...LOCALIZED_STRINGS.LoginSignup} />
                                    </AccountText>
                                </SignInLink>
                            )
                        }
                    </CustomMetrics>

                    <Divider />

                    <CustomMetrics>
                        {
                            ({ sendMetric }) => (
                                <SignInLink
                                    data-element-name="mobile-menu-provider-login"
                                    data-test="mobile-menu-provider-login"
                                    href={DropdownLinks.providerSignin}
                                    onClick={ev => {
                                        sendMetric({
                                            element: 'ProviderLogin',
                                            initiator: EVENT_INITIATORS.USER,
                                            interactionType: INTERACTION_TYPES.CLICK,
                                        });
                                        this.trackSignInLinks(ev);
                                    }}
                                >
                                    <UserType>
                                        <FormattedMessage {...LOCALIZED_STRINGS.Doctors} />
                                    </UserType>
                                    <AccountText>
                                        <FormattedMessage {...LOCALIZED_STRINGS.Login} />
                                    </AccountText>
                                </SignInLink>
                            )
                        }
                    </CustomMetrics>
                </Metrics>
            </SignIn>
        );
    }

    maybeRenderCustomLinks() {
        const { links } = this.props.theme.header.mobileMenu;

        if (links && links.length) {
            return (
                <CustomLinkSection data-test="mobile-menu-custom-links">
                    {links.map((definition, index) => (
                        <Link
                            key={index}
                            href={definition.uri}
                            title={definition.title}
                        >
                            {definition.text}
                        </Link>
                    ))}
                </CustomLinkSection>
            );
        }
    }

    getModifiedCollapsibleSectionThemeFragment() {
        const { theme } = this.props;

        return merge({}, theme, {
            components: {
                CollapsibleSection: {
                    children: {
                        bgColor: navy,
                        borderColor: 'transparent',
                        textColor: white,
                    },
                    header: {
                        bgColor: navy,
                        textColor: white,
                        bgColorExpanded: navy,
                        textColorExpanded: white,
                    },
                },
            },
        });
    }

    renderLink(url, element, component, text) {
        if (!text) {
            text = element;
        }

        return (
            <MetricsClickable
                component={component}
                element={element}
            >
                <Link
                    data-filter-component={component}
                    data-filter-name={element}
                    data-test={`mobile-link-${element}`}
                    href={url}
                    onClick={this.trackGenericMobileLinks}
                >
                    {text}
                </Link>
            </MetricsClickable>
        );
    }

    renderZocdocLink(url, element, text) {
        return this.renderLink(url, element, 'ZocdocLinks', text);
    }

    trackSignInLinks = async ev => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, 'SignInLinks');
    }

    trackGenericMobileLinks = async ev => {
        const element = ev.currentTarget.getAttribute('data-filter-name');
        const component = ev.currentTarget.getAttribute('data-filter-component');
        await this.props.onBeforeLinkClick(element, component);
    };

    trackSocialMediaLinks = async ev => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, 'SocialMediaLinks');
    };

    trackBadgeLinks = async ev => {
        const element = ev.currentTarget.getAttribute('data-link-name');
        await this.props.onBeforeLinkClick(element, 'AppDownload');
    };

    trackSeoLinks = async ev => {
        // Proceed GA Tracking
        const eventTarget = ev.currentTarget;
        const eventTargetText = eventTarget.textContent;
        const element = eventTargetText.replace(/\s/g, '');
        const component = `${eventTarget.getAttribute('data-title')}Links`;
        const { onBeforeLinkClick } = this.props;
        if (onBeforeLinkClick) {
            await onBeforeLinkClick(element, component);
        }
    }
    trackUserLinks = async ev => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, element);
    };

    renderSecondaryContent() {
        if (this.props.theme.header.showPoweredByZocdoc) {
            return (
                <Section
                    data-test="mobile-menu-powered_by_zocdoc"
                    grow
                >
                    <StyledPoweredByZocdoc />
                </Section>
            );
        }

        return (
            <React.Fragment>
                <Section data-test="mobile-menu-zocdoc-links">
                    <SectionHeader>Zocdoc</SectionHeader>
                    {this.renderZocdocLink('/', 'Home')}
                    {this.renderZocdocLink('/about', 'About')}
                    {this.renderZocdocLink('/about/press', 'Press')}
                    {this.renderZocdocLink('/careers', 'Careers')}
                    {this.renderZocdocLink('/contactus', 'Contact')}
                    {this.renderZocdocLink('/about/faq', 'Help')}
                    {this.renderZocdocLink('/about/blog', 'Blogs')}
                    {this.renderZocdocLink('/answers', 'Health Q&A')}
                </Section>

                <SectionSeparator />

                <Section data-test="mobile-menu-cta-links">
                    <SectionHeader>Are you a top doctor or dentist?</SectionHeader>
                    {this.renderLink('/join', 'JoinLink', 'AreYouADoctorOrDentistLinks', 'List your practice on Zocdoc')}
                </Section>

                <SectionSeparator />

                <Section data-test="mobile-menu-hospital-links">
                    <SectionHeader>Zocdoc for health systems</SectionHeader>
                    {this.renderLink('/about/healthsystems', 'LearnMore', 'ZocdocForHealthSystems', 'Learn more')}
                </Section>

                <SectionSeparator />

                <Section>
                    <SectionHeader>Stay connected</SectionHeader>
                    <NestedSection
                        data-test="mobile-menu-social-links"
                        horizontal
                    >
                        <Metrics component="SocialMediaLinks">
                            <MetricsClickable element="Facebook">
                                <FacebookLink onBeforeLinkClick={this.trackSocialMediaLinks} />
                            </MetricsClickable>

                            <MetricsClickable element="Twitter">
                                <TwitterLink onBeforeLinkClick={this.trackSocialMediaLinks} />
                            </MetricsClickable>

                            <MetricsClickable element="GooglePlus">
                                <GooglePlusLink onBeforeLinkClick={this.trackSocialMediaLinks} />
                            </MetricsClickable>

                            <MetricsClickable element="LinkedIn">
                                <LinkedInLink onBeforeLinkClick={this.trackSocialMediaLinks} />
                            </MetricsClickable>
                        </Metrics>
                    </NestedSection>

                    <Metrics component="AppDownload">
                        <NestedSection
                            data-test="mobile-menu-app-links"
                            horizontal
                        >
                            <MetricsClickable element="AppStore">
                                <AppStoreLink onBeforeLinkClick={this.trackBadgeLinks} />
                            </MetricsClickable>

                            <MetricsClickable element="GooglePlay">
                                <GooglePlayLink onBeforeLinkClick={this.trackBadgeLinks} />
                            </MetricsClickable>
                        </NestedSection>
                    </Metrics>

                    <NestedSection data-test="mobile-menu-made_in_ny">
                        <MetricsClickable
                            component="MadeInNy"
                            element="MadeInNy"
                        >
                            <MadeInNYLink onBeforeLinkClick={this.trackBadgeLinks} />
                        </MetricsClickable>
                    </NestedSection>

                </Section>

                <ThemeProvider theme={this.getModifiedCollapsibleSectionThemeFragment()}>
                    <StyledCollapsibleSection
                        autoScroll
                        data-test="mobile-menu-more-links"
                        headerOpenText="less"
                        headerText="more"
                    >
                        <LinkFarm>
                            <LinkFarmRow>
                                <BrowseLinkGroup
                                    data-test="mobile-menu-browse-links"
                                    onBeforeLinkClick={this.trackSeoLinks}
                                />
                                <SearchLinkGroup
                                    data-test="mobile-menu-search-links"
                                    onBeforeLinkClick={this.trackSeoLinks}
                                />
                            </LinkFarmRow>
                            <LinkFarmRow>
                                <SpecialtiesLinkGroup
                                    data-test="mobile-menu-specialty-links"
                                    onBeforeLinkClick={this.trackSeoLinks}
                                />
                                <CitiesLinkGroup
                                    data-test="mobile-menu-city-links"
                                    onBeforeLinkClick={this.trackSeoLinks}
                                />
                            </LinkFarmRow>
                        </LinkFarm>
                    </StyledCollapsibleSection>
                </ThemeProvider>
            </React.Fragment>
        );
    }

    toggleMenu = () => this.setState(state => ({ open: !state.open }))

    handleWindowResize = () => {
        if (window.innerWidth >= Breakpoints.medium && this.state.open) {
            this.toggleMenu();
        }
    }
}

export default withTheme(MobileMenu);

const MenuTriggerArea = styled.div`
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    position: relative;
    margin: 0 20px 0 0;
    padding: 0;
    ${breakpoint.medium} {
        display: none;
    }
`;

const MenuTrigger = styled.button`
    color: transparent;
    background-color: transparent;
    position: absolute;
    border: none;
    top: -22px;
    left: -18px;
    height: 54px;
    width: 50px;

    ${breakpoint.small} {
        left: -36px;
        width: 86px;
    }
`;

const Menu = styled.div`
    align-items: center;
    background-color: ${white};
    display: flex;
    flex-direction: column;
    overflow: auto;
    overflow-x: hidden;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    transform: translate3d(0, 0, 0);

    -webkit-overflow-scrolling: touch;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    > * {
        flex-shrink: 0;
    }
`;

const StyledClose = styled(PaddedContainer)`
    box-sizing: content-box;
    cursor: pointer;
    position: absolute;
    padding-top: 22px;
    padding-bottom: 60px;
    top: 0;
    left: 0;
`;

const Home = styled.div`
    align-items: center;
    background-color: ${p => p.theme.header.mobileMenu.home.bgColor};
    color: ${p => p.theme.header.mobileMenu.home.textColor};
    display: flex;
    font: 20px/1.3 ${FontFamily.SharpSansSemibold};
    justify-content: center;
    position: relative;
    height: 96px;
    width: 100%;
`;

const HomeLink = styled(ZdLink)`
    border-bottom-color: ${p => p.theme.header.mobileMenu.home.borderColor};
    color: inherit;
    display: inline-block;
    letter-spacing: 0.1px;
    margin-left: 10px;
`;

const SignIn = styled.div`
    align-items: center;
    background-color: ${green};
    color: ${navy};
    display: flex;
    font: 18px/1.3 ${FontFamily.SharpSansBold};
    justify-content: space-evenly;
    height: 84px;
    width: 100%;
`;

const SignInLink = styled(ZdLink)`
    width: 50%;
    text-align: center;
    border: none;
    color: ${navy};
`;

const UserType = styled.div`
    display: block;
    font: 16px/18px ${FontFamily.SharpSansBold};
`;

const Divider = styled.div`
    border-left: 1px solid ${navy};
    height: 50px;
`;

const AccountText = styled.div`
    color: inherit;
    display: inline-block;
    letter-spacing: 0.1px;
    font: 14px/15px ${FontFamily.SharpSansSemibold};
    border-bottom: 1px dotted;
`;

const Section = styled.div`
    align-items: center;
    display: flex;
    flex-direction: ${({ horizontal }) => horizontal ? 'row' : 'column'};
    font: 16px/1.3 ${FontFamily.SharpSansSemibold};
    justify-content: center;
    margin: 30px 0;

    flex-grow: ${({ grow }) => grow ? 1 : 0};

    > * {
        flex-shrink: 0;
    }
`;

const NestedSection = styled(Section)`
    margin: 7px 0;

    > * {
        margin: 0 8px;
        padding: 8px;
    }
`;

const SectionSeparator = styled.div`
    background-color: ${navy};
    display: inline-block;
    height: 3px;
    width: 50px;
`;

const SectionHeader = styled.span`
    color: ${navy};
    display: inline-block;
    font: 18px/2 ${FontFamily.SharpSansBold};
`;

const CustomLinkSection = styled(Section)`
    margin: 60px 0;
`;

const Link = styled(ZdLink)`
    display: inline-block;
    margin: 8px 0;
`;

const StyledPoweredByZocdoc = styled(PoweredByZocdoc)`
    color: #00234b;
    font-size: 11px;
`;

const StyledCollapsibleSection = styled(CollapsibleSection)`
    color: ${white};
    width: 100%;
`;

const LinkFarm = styled.div`
    font-size: 13px;
    padding-bottom: 40px;
    width: 100%;

    ${breakpoint.xsmall} {
        display: table;
    }
`;

const LinkFarmRow = styled.div`
    > * {
        margin-top: 20px;
    }

    ${breakpoint.xsmall} {
        display: table-row;

        &:not(:first-child) {
            > * {
                margin-top: 0;
                padding-top: 20px;
            }
        }

        > * {
            display: table-cell;
        }

        > *:first-child {
            padding-right: 5%;
        }

        > *:last-child {
            padding-left: 5%;
        }
    }
`;

const UserSection = styled.div`
    width: 100%;
`;

const UserLinkContainer = styled.div`
    padding: 9px;
`;

const UserLink = styled(ZdLink)`
    height: 56px;
    background-color: ${grey10};
    color: ${navy};
    margin-bottom: 1px;
    display: flex;
    border-bottom: none !important;
    align-items: center;
    justify-content: space-between;
`;

const UserLinkText = styled.div`
    margin-left: 35px;
    font: 18px/20px ${FontFamily.SharpSansBold};
    flex-grow: 1
`;

const UserLinkIcon = styled.div`
    align-items: center;
    display: flex;
    height: 30px;
    justify-content: center;
    line-height: 0px;
    margin-left: 36px;
    width: 30px;
`;

const StyledArrow = styled(UnstyledArrow)`
    margin-right: 30px;
`;
